import { Link } from "react-router-dom";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  BadgeCheckIcon,
  Building2Icon,
  CalendarDaysIcon,
  LockKeyholeIcon,
  ShieldCheckIcon,
  UsersRoundIcon,
} from "lucide-react";

const LOGO_URL =
  "https://yswafknmqpwjrsxjslbu.supabase.co/storage/v1/object/public/logos//logosmall.png";

export default function ClubAccess() {
  return (
    <div className="min-h-screen bg-[#f3f0e7] text-[#1f2d26]">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -right-32 -top-28 h-96 w-96 rounded-full bg-[#dfe9e1] blur-3xl" />
        <div className="absolute -bottom-40 -left-32 h-96 w-96 rounded-full bg-[#eee2c8] blur-3xl" />
      </div>

      <main className="relative mx-auto flex min-h-screen w-full max-w-5xl items-center px-4 py-8 sm:px-6 sm:py-12">
        <section className="w-full overflow-hidden rounded-[30px] border border-[#d8d2c4] bg-[#fffdf8] shadow-[0_24px_70px_rgba(31,45,38,0.12)]">
          <div className="relative overflow-hidden bg-[#183d2d] px-6 py-8 text-white sm:px-10 sm:py-10">
            <div className="pointer-events-none absolute -right-24 -top-20 h-72 w-72 rounded-full bg-[#b08b45]/22 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-32 -left-16 h-72 w-72 rounded-full bg-white/[0.08] blur-3xl" />

            <div className="relative">
              <div className="flex items-center justify-between gap-4">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-xs font-semibold text-white/55 transition hover:text-white"
                >
                  <ArrowLeftIcon className="h-4 w-4" />
                  Till startsidan
                </Link>

                <span className="rounded-full border border-[#d8c38d]/20 bg-[#d8c38d]/10 px-3 py-1.5 text-[9px] font-semibold uppercase tracking-[0.17em] text-[#dfcc97]">
                  Under utveckling
                </span>
              </div>

              <div className="mt-10 flex items-center gap-3">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/15 bg-white/10">
                  <img
                    src={LOGO_URL}
                    alt="HoleInOne"
                    className="h-8 w-8 object-contain brightness-125"
                  />
                </span>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-white/50">
                    HoleInOne.se
                  </p>
                  <p className="mt-0.5 text-sm font-medium text-white/80">
                    Klubbportal
                  </p>
                </div>
              </div>

              <div className="mt-9 max-w-3xl">
                <p className="text-[10px] font-semibold uppercase tracking-[0.23em] text-[#dfcc97]">
                  Ett separat spår för golfklubbar
                </p>
                <h1 className="mt-3 text-3xl font-semibold tracking-tight sm:text-5xl">
                  Klubbkonton behöver andra verktyg än privatkonton.
                </h1>
                <p className="mt-5 max-w-2xl text-sm leading-7 text-white/60 sm:text-base">
                  Därför förbereder vi en egen klubbportal med separata roller,
                  behörigheter och arbetsflöden. Den nuvarande betan är i första
                  hand byggd för privata golfgäng.
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-6 px-5 py-6 sm:px-8 sm:py-8 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9a7735]">
                Planerad inriktning
              </p>
              <h2 className="mt-2 text-2xl font-semibold text-[#183d2d]">
                Förberedda behov i klubbversionen
              </h2>

              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {[
                  {
                    icon: UsersRoundIcon,
                    title: "Roller och behörigheter",
                    text: "Separera klubbadmin, tävlingsledning och funktionärer.",
                  },
                  {
                    icon: CalendarDaysIcon,
                    title: "Flera tävlingar",
                    text: "Hantera återkommande tävlingar och större kalendrar.",
                  },
                  {
                    icon: ShieldCheckIcon,
                    title: "Kontrollerad åtkomst",
                    text: "Klubbdata och administration hålls åtskilda.",
                  },
                  {
                    icon: BadgeCheckIcon,
                    title: "Klubbanpassade flöden",
                    text: "Arbetsflöden utformas efter klubbens verkliga behov.",
                  },
                ].map((item) => {
                  const Icon = item.icon;

                  return (
                    <article
                      key={item.title}
                      className="rounded-2xl border border-[#ddd6c8] bg-white p-4"
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#e7efe9] text-[#183d2d]">
                        <Icon className="h-4 w-4" />
                      </span>
                      <h3 className="mt-4 text-sm font-semibold text-[#183d2d]">
                        {item.title}
                      </h3>
                      <p className="mt-1.5 text-xs leading-5 text-[#68716c]">
                        {item.text}
                      </p>
                    </article>
                  );
                })}
              </div>
            </div>

            <aside className="rounded-[24px] border border-[#d8d2c4] bg-[#f5f1e8] p-5 sm:p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#183d2d] text-white">
                <Building2Icon className="h-5 w-5" />
              </span>

              <h2 className="mt-5 text-xl font-semibold text-[#183d2d]">
                Representerar du en golfklubb?
              </h2>
              <p className="mt-3 text-sm leading-6 text-[#68716c]">
                Lämna en intresseanmälan och beskriv vilka tävlingsflöden,
                roller och funktioner klubben behöver. Det hjälper oss att
                prioritera klubbportalen rätt.
              </p>

              <Link
                to="/ansok-beta?typ=club"
                className="group mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#183d2d] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#214d39]"
              >
                Lämna intresseanmälan
                <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>

              <div className="mt-5 flex items-start gap-3 rounded-xl border border-[#ddd4c3] bg-[#fffdf8] p-3.5">
                <LockKeyholeIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#9a7735]" />
                <p className="text-xs leading-5 text-[#68716c]">
                  Klubbkonton och separat klubbinloggning är ännu inte öppna.
                  Privatkonton loggar in via den vanliga inloggningen.
                </p>
              </div>
            </aside>
          </div>
        </section>
      </main>
    </div>
  );
}